/**
 * Makes the reload that loads a new build look like an ordinary page change.
 *
 * Loading a new build means reloading the page (see utils/appUpdate.ts). On its
 * own that shows the logo splash, then the sign-in loading screen, then the
 * site rebuilding itself around the page: a flash that reads as "something went
 * wrong". Instead:
 *
 * 1. beginUpdateTransition: as soon as the switch starts, the page under the
 *    toolbar is hidden, so the user sees the toolbar and an empty page, the way
 *    any page looks while it loads.
 * 2. saveShellSnapshot, just before the reload: a copy of the site's frame (the
 *    rail, the header, the status strip, the empty page area) and the CSS rules
 *    that draw it go to sessionStorage.
 * 3. The inline script `update-shell-restore` at the top of index.html's body
 *    paints that copy over the page before anything else is drawn, in the same
 *    colours and theme (light or dark) the user had.
 * 4. dismissUpdateShell, called by MainLayout once the page itself has rendered
 *    (or by the inline script after SHELL_MAX_SHOW_MS whatever happens), fades
 *    the copy out over the real site.
 *
 * The copy is inert and hidden from screen readers, used at most once, only on
 * the very next load of the same address, and only within SHELL_MAX_AGE_MS.
 * Anything that goes wrong (no frame on screen, storage full or blocked) falls
 * back to the plain reload and the theme-aware splash.
 */

/** sessionStorage key. index.html's update-shell-restore script reads the same key. */
export const SHELL_STORAGE_KEY = 'dietech.updateShell';
/** A snapshot older than this is ignored (index.html checks it too). */
export const SHELL_MAX_AGE_MS = 30_000;
/** Snapshots larger than this are not saved: the plain splash is used instead. */
export const SHELL_MAX_CHARS = 2_000_000;
/** The frame MainLayout renders: rail, header, page area and status strip. */
export const SHELL_SELECTOR = '.main-layout-grid';

const UPDATING_CLASS = 'app-updating';
const UPDATING_STYLE_ID = 'app-updating-css';
const SHELL_ID = 'update-shell';
const SHELL_CSS_ID = 'update-shell-css';
const SHELL_HTML_CLASS = 'update-shell';

export interface ShellSnapshot {
  v: 1;
  /** Date.now() when it was taken. */
  at: number;
  /** pathname + search it was taken on: the address the reload loads. */
  path: string;
  /** The frame's markup, page area emptied. */
  html: string;
  /** The CSS rules that apply to the frame (and to html/body). */
  css: string;
  /** The <html> element's class and style attributes (theme, dark mode, text size). */
  htmlClass: string;
  htmlStyle: string;
  /**
   * The text styles the frame inherits from <body> (font, size, colour). The copy is
   * painted on <html> before the body exists, so it would otherwise inherit the
   * browser's defaults (a serif font).
   */
  bodyStyle: string;
}

/** Inherited properties the frame takes from <body>. */
const INHERITED_FROM_BODY = [
  'font-family',
  'font-size',
  'font-weight',
  'font-feature-settings',
  'line-height',
  'letter-spacing',
  'color',
  '-webkit-font-smoothing',
  'text-rendering',
];

function inheritedBodyStyle(doc: Document): string {
  const view = doc.defaultView;
  if (!view || !doc.body) return '';
  const computed = view.getComputedStyle(doc.body);
  return INHERITED_FROM_BODY.map((prop) => [prop, computed.getPropertyValue(prop)] as const)
    .filter(([, value]) => value)
    .map(([prop, value]) => `${prop}: ${value};`)
    .join(' ');
}

/**
 * Hides the page under the toolbar while the new build takes over (a fraction
 * of a second, normally). The page the user was going to opens in the new
 * build; showing the old build's copy of it first, then replacing it, is the
 * flash this avoids.
 */
export function beginUpdateTransition(doc: Document = document): void {
  if (!doc.getElementById(UPDATING_STYLE_ID)) {
    const style = doc.createElement('style');
    style.id = UPDATING_STYLE_ID;
    // The page goes; after a moment a spinner shows the next one is coming (as the copy of the
    // frame does after the reload, see index.html), in case the new build is slow to take over.
    style.textContent = [
      `html.${UPDATING_CLASS} #main-content .page-panel > * { visibility: hidden !important; }`,
      `html.${UPDATING_CLASS} #main-content::after { content: ''; position: fixed; left: 50%; top: 40%;`
        + ' width: 36px; height: 36px; margin-left: -18px; box-sizing: border-box; border-radius: 50%;'
        + ' border: 3px solid rgba(100, 116, 139, 0.25); border-top-color: #64748b; opacity: 0;'
        + ' animation: app-updating-spin 0.9s linear infinite, app-updating-in 0.2s ease 0.6s forwards; }',
      '@keyframes app-updating-spin { to { transform: rotate(360deg); } }',
      '@keyframes app-updating-in { to { opacity: 1; } }',
    ].join('\n');
    doc.head.appendChild(style);
  }
  doc.documentElement.classList.add(UPDATING_CLASS);
}

/** Shows the page again: the reload did not happen (the user stayed when the browser asked). */
export function endUpdateTransition(doc: Document = document): void {
  doc.documentElement.classList.remove(UPDATING_CLASS);
}

// ---------------------------------------------------------------------------
// Snapshot
// ---------------------------------------------------------------------------

/** Splits a selector list on its top-level commas. */
export function splitSelectorList(selector: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let quote = '';
  let start = 0;
  for (let i = 0; i < selector.length; i++) {
    const ch = selector[i];
    if (quote) {
      if (ch === '\\') i++;
      else if (ch === quote) quote = '';
      continue;
    }
    if (ch === '\\') { i++; continue; }
    if (ch === '"' || ch === "'") quote = ch;
    else if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;
    else if (ch === ',' && depth === 0) {
      parts.push(selector.slice(start, i).trim());
      start = i + 1;
    }
  }
  parts.push(selector.slice(start).trim());
  return parts.filter(Boolean);
}

/**
 * The selector with the parts that depend on state or draw generated content
 * removed, so it can be tested against the elements on screen:
 * `.bi-house::before` -> `.bi-house`, `a:hover > span` -> `a > span`.
 */
export function probeSelector(selector: string): string {
  const probe = selector
    .replace(/::?(?:before|after|first-line|first-letter|placeholder|selection|marker|backdrop|-webkit-[\w-]+|-moz-[\w-]+|-ms-[\w-]+)(?:\([^)]*\))?/gi, '')
    .replace(/:(?:hover|focus-visible|focus-within|focus|active|visited|link|target)(?![\w-])/gi, '')
    .trim();
  return probe || '*';
}

/** The classes the last compound of a selector requires: `.a .b.c` -> ['b', 'c']. Null when it cannot tell. */
export function lastCompoundClasses(selector: string): string[] | null {
  if (/[()[\]\\]/.test(selector)) return null;
  const last = selector.split(/[\s>+~]+/).filter(Boolean).pop() ?? '';
  return Array.from(last.matchAll(/\.([\w-]+)/g), (m) => m[1]);
}

interface Matcher {
  matters(selectorText: string): boolean;
}

function matcherFor(frame: Element, doc: Document): Matcher {
  const html = doc.documentElement;
  const body = doc.body;
  const classes = new Set<string>();
  for (const el of [html, body, frame, ...Array.from(frame.querySelectorAll('*'))]) {
    el.classList.forEach((c) => classes.add(c));
  }
  const hits = (probe: string) =>
    frame.matches(probe) || !!frame.querySelector(probe) || html.matches(probe) || body.matches(probe);
  return {
    matters(selectorText) {
      for (const part of splitSelectorList(selectorText)) {
        const probe = probeSelector(part);
        const needed = lastCompoundClasses(probe);
        // Most rules (every DevExtreme widget, every other page) are about classes the frame
        // does not have: skip them without asking the browser to match.
        if (needed && needed.some((c) => !classes.has(c))) continue;
        try {
          if (hits(probe)) return true;
        } catch {
          return true; // a selector the probe broke: keep the rule rather than lose it
        }
      }
      return false;
    },
  };
}

// CSSRule.type values (the constants are deprecated, the numbers are not going anywhere).
const STYLE_RULE = 1;
const MEDIA_RULE = 4;
const KEYFRAMES_RULE = 7;
const SUPPORTS_RULE = 12;

function collectRules(rules: CSSRuleList, matcher: Matcher): string {
  let out = '';
  for (const rule of Array.from(rules)) {
    if (rule.type === STYLE_RULE) {
      if (matcher.matters((rule as CSSStyleRule).selectorText)) out += rule.cssText + '\n';
    } else if (rule.type === MEDIA_RULE) {
      const inner = collectRules((rule as CSSMediaRule).cssRules, matcher);
      if (inner) out += `@media ${(rule as CSSMediaRule).media.mediaText}{\n${inner}}\n`;
    } else if (rule.type === SUPPORTS_RULE) {
      const inner = collectRules((rule as CSSSupportsRule).cssRules, matcher);
      if (inner) out += `@supports ${(rule as CSSSupportsRule).conditionText}{\n${inner}}\n`;
    } else if (rule.type === KEYFRAMES_RULE) {
      out += rule.cssText + '\n';
    } else if ('cssRules' in rule && 'name' in rule && rule.constructor?.name === 'CSSLayerBlockRule') {
      const inner = collectRules((rule as CSSLayerBlockRule).cssRules, matcher);
      if (inner) out += `@layer ${(rule as CSSLayerBlockRule).name}{\n${inner}}\n`;
    }
    // @font-face and @import are left out: the new build's own stylesheet declares
    // the fonts, and the old build's font files may already be gone from the server.
  }
  return out;
}

/** The CSS rules that draw `frame` (and html/body), from every stylesheet on the page. */
export function collectShellCss(frame: Element, doc: Document = document): string {
  const matcher = matcherFor(frame, doc);
  const out: string[] = [];
  for (const sheet of Array.from(doc.styleSheets)) {
    if (sheet.disabled) continue;
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // another origin's stylesheet: not readable, and not ours
    }
    const text = collectRules(rules, matcher);
    if (!text) continue;
    const media = sheet.media?.mediaText;
    out.push(media && media !== 'all' ? `@media ${media}{\n${text}}` : text);
  }
  return out.join('\n');
}

const DROP_ELEMENTS = 'script, iframe, object, embed, video, audio, canvas, template';

/** The frame's markup with the page area emptied, ids and handlers removed. */
export function shellMarkup(frame: Element): string {
  const clone = frame.cloneNode(true) as Element;
  clone.querySelectorAll(DROP_ELEMENTS).forEach((n) => n.remove());
  const panel = clone.querySelector('.page-panel');
  if (panel) {
    panel.replaceChildren();
    const spinner = frame.ownerDocument.createElement('div');
    spinner.className = 'update-shell-spinner';
    panel.appendChild(spinner);
  }
  for (const el of [clone, ...Array.from(clone.querySelectorAll('*'))]) {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      // Ids would duplicate the real ones (#main-content) while both are on the page.
      if (name === 'id' || name === 'autofocus' || name.startsWith('on')) el.removeAttribute(attr.name);
    }
  }
  return clone.outerHTML;
}

/** A snapshot of the frame on screen now, or null when there is none (sign-in, tablet screens). */
export function captureShellSnapshot(doc: Document = document, now: number = Date.now()): ShellSnapshot | null {
  const frame = doc.querySelector(SHELL_SELECTOR);
  if (!frame) return null;
  const root = doc.documentElement;
  const htmlClass = Array.from(root.classList)
    .filter((c) => c !== UPDATING_CLASS && c !== SHELL_HTML_CLASS)
    .join(' ');
  const loc = doc.defaultView?.location;
  return {
    v: 1,
    at: now,
    path: loc ? loc.pathname + loc.search : '',
    html: shellMarkup(frame),
    css: collectShellCss(frame, doc),
    htmlClass,
    htmlStyle: root.getAttribute('style') ?? '',
    bodyStyle: inheritedBodyStyle(doc),
  };
}

/** Saves the snapshot for the next load. Returns whether it was saved. */
export function saveShellSnapshot(
  doc: Document = document,
  storage: Pick<Storage, 'setItem' | 'removeItem'> | null = safeSessionStorage(),
): boolean {
  if (!storage) return false;
  try {
    const snap = captureShellSnapshot(doc);
    if (!snap) {
      storage.removeItem(SHELL_STORAGE_KEY);
      return false;
    }
    const json = JSON.stringify(snap);
    if (json.length > SHELL_MAX_CHARS) {
      storage.removeItem(SHELL_STORAGE_KEY);
      return false;
    }
    storage.setItem(SHELL_STORAGE_KEY, json);
    return true;
  } catch {
    // Quota or blocked storage, or a stylesheet the browser would not walk: plain reload.
    try { storage.removeItem(SHELL_STORAGE_KEY); } catch { /* nothing more to do */ }
    return false;
  }
}

/** Fades out the copy of the frame painted by index.html, if there is one. */
export function dismissUpdateShell(doc: Document = document): void {
  const shell = doc.getElementById(SHELL_ID);
  const css = doc.getElementById(SHELL_CSS_ID);
  doc.documentElement.classList.remove(SHELL_HTML_CLASS);
  if (!shell) {
    css?.remove();
    return;
  }
  if (shell.getAttribute('data-leaving')) return;
  shell.setAttribute('data-leaving', 'true');
  shell.style.transition = 'opacity 120ms ease-out';
  shell.style.opacity = '0';
  setTimeout(() => {
    shell.remove();
    css?.remove();
  }, 140);
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}
